<template>
  <Transition name="fade">
    <div v-if="open" class="blur" @click="close"></div>
  </Transition>
  <Transition
    v-resize="onResize"
    name="slide"
    @enter="slideIn"
    @leave="slideOut"
  >
    <div v-if="open" ref="panel" class="panel" :class="{ 'panel--open': open }">
      <div ref="panel-header" class="panel__header">
        <span>{{ heading }}</span>
        <IconButton icon="cross" @click="close" />
      </div>
      <div
        ref="panel-content"
        class="panel__content"
        :class="{ 'panel__content--no-padding': !contentPadding }"
      >
        <slot name="content" />
      </div>
      <div v-if="!!$slots.footer" ref="panel-footer" class="panel__footer">
        <slot name="footer" />
      </div>
    </div>
  </Transition>
</template>

<script>
import IconButton from "@/components/IconButton.vue";

export default {
  name: "SlideIn",
  components: {
    IconButton,
  },
  inject: ["mq"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    heading: {
      type: String,
      required: true,
    },
    desktopSide: {
      type: String,
      default: "right",
      validator(value) {
        return ["right", "left"].includes(value);
      },
    },
    contentPadding: {
      type: Boolean,
      default: true,
    },
    lockScroll: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["close"],
  methods: {
    close() {
      this.$emit("close", false);
    },
    slideIn(element) {
      if (this.lockScroll) {
        const siteHtml = document.getElementsByTagName("html")[0];
        siteHtml.classList.add("slideup-lock-scroll");
      }
      this.$nextTick(() => {
        if (this.mq.mdPlus) {
          element.style[this.desktopSide] = "auto";

          const width = getComputedStyle(element).width;
          element.style[this.desktopSide] = "-" + width;

          requestAnimationFrame(() => {
            element.style[this.desktopSide] = "0";
          });
        } else {
          element.style.bottom = "auto";

          const height = getComputedStyle(element).height;
          element.style.bottom = "-" + height;

          this.setMaxHeight(element.offsetHeight);

          requestAnimationFrame(() => {
            element.style.bottom = "0";
          });
        }
      });
    },
    slideOut(element) {
      if (this.lockScroll) {
        const siteHtml = document.getElementsByTagName("html")[0];
        siteHtml.classList.remove("slideup-lock-scroll");
      }

      if (this.mq.mdPlus) {
        element.style[this.desktopSide] = "-" + getComputedStyle(element).width;
      } else {
        element.style.bottom = "-" + getComputedStyle(element).height;
      }
    },
    setMaxHeight(height) {
      const windowHeight = window.innerHeight;
      const bufferPadding = 70;
      const headerHeight = this.$refs["panel-header"].offsetHeight || 0;
      const footerHeight = this.$refs["panel-footer"]?.offsetHeight || 0;

      if (height + bufferPadding >= windowHeight) {
        const newContentHeight =
          windowHeight - bufferPadding - headerHeight - footerHeight;
        this.$refs["panel-content"].style.maxHeight = newContentHeight + "px";
      }
    },
    onResize({ height }) {
      this.setMaxHeight(height);
    },
  },
};
</script>

<style lang="scss" scoped>
.panel {
  direction: ltr;
  transform: translate3d(0, 0, 0);
  border-radius: 10px 10px 0 0;
  overflow: hidden;
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  background: $color-white;
  height: auto;
  width: 100%;
  z-index: 4;
  transition: bottom 0.5s;
  transition-timing-function: ease-in;

  @include respond-above(md) {
    width: 360px;
    left: auto;
    right: auto;
    bottom: auto;
    top: 0;
    border-radius: 0;
    transition: left 0.5s, right 0.5s;
    display: flex;
    flex-direction: column;
    max-height: 100%;
  }

  &--open {
    transition-timing-function: ease-out;
  }

  &__header {
    padding: 0 20px;
    font-size: 16px;
    font-family: $heading-font-family;
    text-transform: uppercase;
    background-color: $color-light;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include respond-above(md) {
      flex: 0 0 auto;
    }

    & > span {
      padding: 16px 0;
      @include t__editorial-headline-10;
    }
  }

  &__footer {
    padding: 20px;
    background-color: $color-light;
    border-top: 1px solid rgba($color-dark, 0.2);

    @include respond-above(md) {
      flex: 0 0 auto;
    }
  }

  &__content {
    padding: 20px;
    overflow-y: scroll;

    @include respond-above(md) {
      flex: 0 1 auto;
    }

    &--no-padding {
      padding: 0;
    }
  }
}

.blur {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
